/**src\di\container.ts
 * Dependency Injection Container
 *
 * Central container that manages all dependencies and their lifecycle.
 * Implements the Singleton pattern to ensure a single instance across the app.
 *
 * This is the heart of Clean Architecture - it wires all layers together:
 * - Infrastructure implementations
 * - Domain repositories
 * - Application use cases
 */

import { AxiosHttpClient } from '@infrastructure/http';
import type { IVisitaRepository } from '@/domain/repositories/IVisitaRepository';
import { VisitaRepository } from '@/infrastructure/repositories/VisitaRepository';
import { GetVisitasUseCase } from '@/application/use-cases/visita/GetVisitasUseCase';
import { CreateVisitaUseCase } from '@/application/use-cases/visita/CreateVisitaUseCase';
import { UpdateVisitaFechaUseCase } from '@/application/use-cases/visita/UpdateVisitaFechaUseCase';
import { LocalStorageAdapter } from '@infrastructure/storage';
import { UserRepository } from '@infrastructure/repositories';
import { AuthService } from '@infrastructure/services';
import { GetUserUseCase, CreateUserUseCase } from '@application/use-cases';
import type { IHttpClient, IStorage } from '@application/ports';
import type { ICongrupoRepository, IIncidenteRepository, ISimpleRepository, IUserRepository, IEquipoRepository, IRepuestoRepository, IUbicacionRepository } from '@domain/repositories';
import type { IAuthService } from '@domain/services';
import { LoginUseCase } from '@application/use-cases/auth/LoginUseCase';

import { IncidenteRepository } from '@/infrastructure/repositories/IncidenteRepository';
import { SimpleRepository } from '@/infrastructure/repositories/SimpleRepository';

import { GetIncidentesUseCase } from '@application/use-cases/incidente/GetIncidentesUseCase';
import { CongrupoRepository } from '@/infrastructure/repositories/CongrupoRepository';
import { EquipoRepository } from '@/infrastructure/repositories/EquipoRepository';
import { GetEquiposUseCase } from '@application/use-cases/equipo/GetEquipoUseCase';
import { CreateIncidenteUseCase } from '@/application/use-cases/incidente/PostIncidenteUseCase';
import { GetIncidenteByIdUseCase } from '@/application/use-cases/incidente/GetIncidenteByIdUseCase';
import { UpdateIncidenteUseCase } from '@/application/use-cases/incidente/PutIncidenteUseCase';
import { GetEquipoByIdUseCase } from '@/application/use-cases/equipo/GetEquipoByIdUseCase';
import { CreateEquipoUseCase } from '@/application/use-cases/equipo/PostEquipoUseCase';
import { UpdateEquipoUseCase } from '@/application/use-cases/equipo/PutEquipoUseCase';
import { GetSimplesUseCase } from '@/application/use-cases/simple/GetSimpleUseCase';
import { CreateSimpleUseCase } from '@/application/use-cases/simple/CreateSimpleUseCase';
import { GetCongrupoUseCase } from '@/application/use-cases/congrupo/GetCongrupoUseCase';
import { DeleteSimpleUseCase } from '@/application/use-cases/simple/DeleteSimpleUseCase';
import { PatchIncidenteUseCase } from '@/application/use-cases/incidente/PatchIncidenteUseCase';
import { GetRepuestoUseCase } from '@/application/use-cases/repuestos/GetRepuestoUseCase';
import { GetRepuestosUseCase } from '@/application/use-cases/repuestos/GetRepuestosUseCase';
import { PatchRepuestoUseCase } from '@/application/use-cases/repuestos/PatchRepuestoUseCase';

import { PostRepuestoUseCase } from '@/application/use-cases/repuestos/PostRepuestoUseCase';
import { RepuestosRepository } from '@/infrastructure/repositories/RepuestosRepository';
import { GetUbicacionByIdUseCase } from '@/application/use-cases/ubicacion/GetUbicacionUseCase';
import { PostUbicacionUseCase } from '@/application/use-cases/ubicacion/PostUbicacionUseCase';
import { UpdateUbicacionUseCase } from '@/application/use-cases/ubicacion/PutUbicacionUseCase';
import { UbicacionRepository } from '@/infrastructure/repositories/UbicacionRepository';
import { GetUbicacionesUseCase } from '@/application/use-cases/ubicacion/GetUbicacionesUseCase';


/**
 * Service Registry - Maps service names to their types
 */
interface ServiceRegistry {
  IVisitaRepository: IVisitaRepository;
  GetVisitasUseCase: GetVisitasUseCase;
  CreateVisitaUseCase: CreateVisitaUseCase;
  UpdateVisitaFechaUseCase: UpdateVisitaFechaUseCase;
  // Infrastructure - Ports
  IHttpClient: IHttpClient;
  IStorage: IStorage;

  // Repositories
  IUserRepository: IUserRepository;
  IIncidenteRepository: IIncidenteRepository;
  ISimpleRepository: ISimpleRepository;
  ICongrupoRepository: ICongrupoRepository;
  IEquipoRepository: IEquipoRepository;
  IRepuestoRepository: IRepuestoRepository;
  IUbicacionRepository: IUbicacionRepository;
  // Domain Services
  IAuthService: IAuthService;

  // Use Cases
  GetUserUseCase: GetUserUseCase;
  CreateUserUseCase: CreateUserUseCase;
  LoginUseCase: LoginUseCase;

  GetSimplesUseCase: GetSimplesUseCase;
  CreateSimpleUseCase: CreateSimpleUseCase;       
  DeleteSimpleUseCase: DeleteSimpleUseCase;

  GetCongrupoUseCase: GetCongrupoUseCase;

  GetEquipoUseCase: GetEquiposUseCase;
  GetEquipoByIdUseCase: GetEquipoByIdUseCase;
  CreateEquipoUseCase: CreateEquipoUseCase;
  UpdateEquipoUseCase: UpdateEquipoUseCase;

  CreateIncidenteUseCase: CreateIncidenteUseCase;
  GetIncidenteByIdUseCase: GetIncidenteByIdUseCase;
  UpdateIncidenteUseCase: UpdateIncidenteUseCase;
  GetIncidentesUseCase: GetIncidentesUseCase;
  PatchIncidenteUseCase: PatchIncidenteUseCase;

  GetRepuestoUseCase: GetRepuestoUseCase; 
  GetRepuestosUseCase: GetRepuestosUseCase;
  PatchRepuestoUseCase: PatchRepuestoUseCase;
  PostRepuestoUseCase: PostRepuestoUseCase;

  GetUbicacionByIdUseCase: GetUbicacionByIdUseCase; 
  GetUbicacionesUseCase: GetUbicacionesUseCase;
  PostUbicacionUseCase: PostUbicacionUseCase;
  UpdateUbicacionUseCase: UpdateUbicacionUseCase;
}

/**
 * Dependency Injection Container
 */
class DIContainer {
  private static instance: DIContainer;
  private services: Map<keyof ServiceRegistry, unknown> = new Map();

  private constructor() {
    this.registerServices();
  }

  /**
   * Get the singleton instance of the container
   */
  public static getInstance(): DIContainer {
    if (!DIContainer.instance) {
      DIContainer.instance = new DIContainer();
    }
    return DIContainer.instance;
  }

  /**
   * Register all services and their dependencies
   */
  private registerServices(): void {
    // Infrastructure - HTTP Client
    this.services.set('IHttpClient', new AxiosHttpClient());
    const visitaRepository = new VisitaRepository(this.get<IHttpClient>('IHttpClient'));
    this.register('IVisitaRepository', visitaRepository);
    this.register('GetVisitasUseCase', new GetVisitasUseCase(visitaRepository));
    this.register('CreateVisitaUseCase', new CreateVisitaUseCase(visitaRepository));
    this.register('UpdateVisitaFechaUseCase', new UpdateVisitaFechaUseCase(visitaRepository));

    // Infrastructure - Storage
    this.services.set('IStorage', new LocalStorageAdapter());

    // Repositories
    this.services.set(
      'IUserRepository',
      new UserRepository(this.get<IHttpClient>('IHttpClient'))
    );

    // Domain Services
    this.services.set(
      'IAuthService',
      new AuthService(
        this.get<IHttpClient>('IHttpClient'),
        this.get<IStorage>('IStorage')
      )
    );

    // Use Cases - User
    this.services.set(
      'GetUserUseCase',
      new GetUserUseCase(this.get<IUserRepository>('IUserRepository'))
    );

    this.services.set(
      'CreateUserUseCase',
      new CreateUserUseCase(this.get<IUserRepository>('IUserRepository'))
    );

    // Use Cases - Auth
    this.services.set(
      'LoginUseCase',
      new LoginUseCase(this.get<IAuthService>('IAuthService'))
    );
    this.services.set(
      'IIncidenteRepository',
      new IncidenteRepository(
        this.get<IHttpClient>('IHttpClient')
      )

    );
    this.services.set(
      'IRepuestoRepository',
      new RepuestosRepository(
        this.get<IHttpClient>('IHttpClient')
      )

    );
    this.services.set(
      'GetIncidentesUseCase',
      new GetIncidentesUseCase(
        this.get<IIncidenteRepository>(
          'IIncidenteRepository'
        )
      ));
    this.services.set(
      'ISimpleRepository',
      new SimpleRepository(
        this.get<IHttpClient>('IHttpClient')
      )
    );
    this.register(
      'IUbicacionRepository',
      new UbicacionRepository(
        this.get<IHttpClient>('IHttpClient')
      )
    );
    this.services.set(
      'GetSimplesUseCase',
      new GetSimplesUseCase(
        this.get<ISimpleRepository>(
          'ISimpleRepository'
        )
      )
    );
    this.services.set(
      'ICongrupoRepository',
      new CongrupoRepository(
        this.get<IHttpClient>('IHttpClient')
      )
    );

    this.services.set(
      'GetCongrupoUseCase',
      new GetCongrupoUseCase(
        this.get<ICongrupoRepository>(
          'ICongrupoRepository'
        )
      )
    );
    this.services.set(
      'IEquipoRepository',
      new EquipoRepository(this.get('IHttpClient'))
    );

    this.services.set(
      'GetEquipoUseCase',
      new GetEquiposUseCase(
        this.get<IEquipoRepository>(
          'IEquipoRepository'
        )
      )
    );
    this.services.set(
      "CreateIncidenteUseCase",
      new CreateIncidenteUseCase(
        this.get<IIncidenteRepository>("IIncidenteRepository")
      )
    );
    this.services.set(
      'GetIncidenteByIdUseCase',
      new GetIncidenteByIdUseCase(
        this.get<IIncidenteRepository>('IIncidenteRepository')
      )
    );
    this.services.set(
      "UpdateIncidenteUseCase",
      new UpdateIncidenteUseCase(
        this.get<IIncidenteRepository>("IIncidenteRepository")
      )
    );
    this.services.set(
      'GetEquipoByIdUseCase',
      new GetEquipoByIdUseCase(
        this.get<IEquipoRepository>('IEquipoRepository')
      )
    );
    this.services.set(
      'CreateEquipoUseCase',
      new CreateEquipoUseCase(
        this.get<IEquipoRepository>('IEquipoRepository')
      )
    );
    this.services.set(
      'UpdateEquipoUseCase',
      new UpdateEquipoUseCase(
        this.get<IEquipoRepository>('IEquipoRepository')
      )
    );
    this.services.set(
      'CreateSimpleUseCase',
      new CreateSimpleUseCase(
        this.get<ISimpleRepository>('ISimpleRepository')
      )
    );
    this.services.set(
      'DeleteSimpleUseCase',
      new DeleteSimpleUseCase(
        this.get<ISimpleRepository>('ISimpleRepository')
      )
    );
    this.services.set(
      'PatchIncidenteUseCase',
      new PatchIncidenteUseCase(
        this.get<IIncidenteRepository>('IIncidenteRepository')
      )
    );
    this.services.set(
      'PatchRepuestoUseCase',
      new PatchRepuestoUseCase(
        this.get<IRepuestoRepository>('IRepuestoRepository')
      )
    );
    this.services.set(
      'GetRepuestosUseCase',
      new GetRepuestosUseCase(
        this.get<IRepuestoRepository>('IRepuestoRepository')
      )
    );
    this.services.set(
      'GetRepuestoUseCase',
      new GetRepuestoUseCase(
        this.get<IRepuestoRepository>('IRepuestoRepository')
      )
    );
    this.services.set(
      'PostRepuestoUseCase',
      new PostRepuestoUseCase(
        this.get<IRepuestoRepository>('IRepuestoRepository')
      )
    );
    this.services.set(
      'GetUbicacionesUseCase',
      new GetUbicacionesUseCase(
        this.get<IUbicacionRepository>('IUbicacionRepository')
      )
    );
    this.services.set(
      'GetUbicacionByIdUseCase',
      new GetUbicacionByIdUseCase(
        this.get<IUbicacionRepository>('IUbicacionRepository')
      )
    );
    this.services.set(
      'PostUbicacionUseCase',
      new PostUbicacionUseCase(
        this.get<IUbicacionRepository>('IUbicacionRepository')
      )
    );
    this.services.set(
      'UpdateUbicacionUseCase',
      new UpdateUbicacionUseCase(
        this.get<IUbicacionRepository>('IUbicacionRepository')
      )
    );
  }

  /**
   * Get a service from the container
   */
  public get<T>(key: keyof ServiceRegistry): T {
    const service = this.services.get(key);
    if (!service) {
      throw new Error(`Service "${String(key)}" not found in DI container`);
    }
    return service as T;
  }

  /**
   * Register a new service or override an existing one
   * Useful for testing or runtime configuration
   */
  public register<K extends keyof ServiceRegistry>(
    key: K,
    instance: ServiceRegistry[K]
  ): void {
    this.services.set(key, instance);
  }

  /**
   * Check if a service is registered
   */
  public has(key: keyof ServiceRegistry): boolean {
    return this.services.has(key);
  }

  /**
   * Clear all services (useful for testing)
   */
  public clear(): void {
    this.services.clear();
  }

  /**
   * Reset container to initial state
   */
  public reset(): void {
    this.clear();
    this.registerServices();
  }
}

/**
 * Export singleton instance for convenience
 */
export const container = DIContainer.getInstance();

/**
 * Export the class for testing purposes
 */
export { DIContainer };
